import { escapeReferenceToken } from '@criteria/json-pointer'
import {
  HTTPSecurityScheme as HTTPSecuritySchemeV3_0,
  SecurityScheme as SecuritySchemeV3_0
} from '@criteria/openapi/v3.0'
import {
  HTTPSecurityScheme as HTTPSecuritySchemeV3_1,
  SecurityScheme as SecuritySchemeV3_1
} from '@criteria/openapi/v3.1'
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
  Button,
  Subheading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text
} from '@criteria-labs/react'
import { PlusCircleIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { filterKeys } from '../../lib/filterKeys'
import { Section } from '../Section'
import { DeleteSecurityScheme, type SecuritySchemeRef } from '../changes/security-scheme'
import { useDispatchChange } from '../useDispatchChange'
import { useRouter } from '../router'

export function HTTPSecuritySchemesSection({
  securitySchemes
}: {
  securitySchemes: Record<string, SecuritySchemeV3_0 | SecuritySchemeV3_1>
}) {
  const dispatchChange = useDispatchChange()
  const [deletingSecurityScheme, setDeletingSecurityScheme] = useState<string | null>(null)
  const httpSecuritySchems = filterKeys(securitySchemes, (key) => securitySchemes[key].type === 'http') as Record<
    string,
    HTTPSecuritySchemeV3_0 | HTTPSecuritySchemeV3_1
  >
  const { push } = useRouter()
  return (
    <Section>
      <div className="mb-3 mt-6 flex items-center justify-start gap-x-1">
        <Subheading>HTTP Authentication Configurations</Subheading>
        <Button plain onClick={() => push('/new-http-auth')}>
          <PlusCircleIcon />
        </Button>
      </div>
      {Object.entries(httpSecuritySchems).length === 0 && <Text>No HTTP authentication configurations.</Text>}
      {Object.entries(httpSecuritySchems).length > 0 && (
        <Table dense bleed className="[--gutter:0]">
          <TableHead>
            <TableRow>
              <TableHeader>Security Scheme Name</TableHeader>
              <TableHeader>HTTP Authentication Scheme</TableHeader>
              <TableHeader>Bearer Format</TableHeader>
              <TableHeader className="relative w-0">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(httpSecuritySchems).map(([name, securityScheme]) => (
              <TableRow key={name} href="#">
                <TableCell>{name}</TableCell>
                <TableCell>{securityScheme.scheme}</TableCell>
                <TableCell>{securityScheme.bearerFormat}</TableCell>
                <TableCell>
                  <Button
                    plain
                    aria-label="Delete authentication scheme"
                    onClick={() => setDeletingSecurityScheme(name)}
                  >
                    <XMarkIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Alert open={deletingSecurityScheme !== null} onClose={() => setDeletingSecurityScheme(null)}>
        <AlertTitle>Delete HTTP Authentication Configuration?</AlertTitle>
        {typeof deletingSecurityScheme === 'string' && (
          <>
            <AlertDescription>
              <p>The security scheme named {deletingSecurityScheme} will be deleted</p>
            </AlertDescription>
            <AlertActions>
              <Button plain onClick={() => setDeletingSecurityScheme(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const ref: SecuritySchemeRef = `#/components/securitySchemes/${escapeReferenceToken(
                    deletingSecurityScheme
                  )}`
                  dispatchChange(new DeleteSecurityScheme(ref))
                  setDeletingSecurityScheme(null)
                }}
              >
                Delete
              </Button>
            </AlertActions>
          </>
        )}
      </Alert>
    </Section>
  )
}
